import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle, Play, Trophy, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import FlashNumberGame from './FlashNumberGame';
import MathOperationsGame from './MathOperationsGame';

const API = process.env.REACT_APP_BACKEND_URL || '';

const StudentLiveExam = ({ user }) => {
  const [activeSessions, setActiveSessions] = useState([]);
  const [completedSessions, setCompletedSessions] = useState([]);
  const [myResults, setMyResults] = useState({});
  const [playingSession, setPlayingSession] = useState(null);

  useEffect(() => {
    loadSessions();
    
    // Poll every 2 seconds for updates
    const interval = setInterval(loadSessions, 2000);
    return () => clearInterval(interval);
  }, []);

  const loadSessions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/exam-sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const active = response.data.filter(s => s.status !== 'completed');
      const completed = response.data.filter(s => s.status === 'completed');
      
      setActiveSessions(active);
      setCompletedSessions(completed);
      
      // Load results for completed sessions
      completed.forEach(session => {
        loadMyResult(session.id);
      });
      
      // Auto-launch game if exam started and not already completed
      const startedSession = active.find(s => 
        s.status === 'in_progress' && 
        !sessionStorage.getItem(`exam_completed_${s.id}`)
      );
      if (startedSession && !playingSession) {
        console.log('Auto-launching exam game:', startedSession);
        setPlayingSession(startedSession);
      }
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
  };

  const loadMyResult = async (sessionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/exam-sessions/${sessionId}/results`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyResults(prev => ({ ...prev, [sessionId]: response.data.my_result }));
    } catch (error) {
      console.error('Error loading result:', error);
    }
  };

  const handleMarkReady = async (sessionId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/exam-sessions/${sessionId}/student-ready`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('You are marked as ready! Waiting for teacher to start the exam...');
      loadSessions();
    } catch (error) {
      console.error('Error marking ready:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark ready');
    }
  };

  const getMyReadyStatus = (session) => {
    return session.student_ready_status?.[user.id] === true;
  };

  const handleExamComplete = async (scoreData) => {
    if (!playingSession) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/exam-sessions/${playingSession.id}/submit-result`,
        {
          score: scoreData.score,
          time_taken: scoreData.time_taken,
          answers: scoreData.answers || {}
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Exam completed! Your results have been submitted.');
      // Mark as completed to prevent re-launching
      sessionStorage.setItem(`exam_completed_${playingSession.id}`, 'true');
      setPlayingSession(null);
      loadSessions();
    } catch (error) {
      console.error('Error submitting exam result:', error);
      toast.error('Failed to submit exam result. Please contact your teacher.');
    }
  };

  // If student is playing exam game, show the game full screen
  if (playingSession) {
    const GameComponent = playingSession.game_type === 'flash_number' 
      ? FlashNumberGame 
      : playingSession.game_type === 'math_operations'
      ? MathOperationsGame
      : null;

    if (!GameComponent) {
      return <div className="p-6 text-center">Game type not supported</div>;
    }

    // Prepare exam config for the game
    const examConfig = {
      autoStart: true,
      ...playingSession.game_config
    };

    console.log('Rendering game component:', {
      gameType: playingSession.game_type,
      examConfig,
      GameComponent: GameComponent?.name
    });

    return (
      <div key={playingSession.id} className="min-h-screen bg-gray-50 p-6">
        <Card className="p-6 mb-4 bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-blue-900">🎯 Exam in Progress</h3>
              <p className="text-sm text-blue-700">Complete the game to submit your results</p>
            </div>
            <div className="text-sm text-blue-600">
              Teacher: <span className="font-semibold">{playingSession.teacher_name}</span>
            </div>
          </div>
        </Card>
        <GameComponent 
          key={playingSession.id}
          user={user} 
          examMode={true}
          examConfig={examConfig}
          onExamComplete={handleExamComplete}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Active Exams */}
      {activeSessions.length > 0 && (
        <Card className="p-6 shadow-xl border-0 bg-gradient-to-br from-blue-50 via-white to-indigo-50">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
              <AlertCircle className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Active Exam Sessions
              </h3>
              <p className="text-sm text-gray-600 mt-1">Click "Ready" when you're prepared to start</p>
            </div>
          </div>

          <div className="space-y-4">
            {activeSessions.map(session => (
              <Card key={session.id} className="p-5 border-2 border-blue-200 bg-white hover:shadow-lg transition-all">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-lg font-bold text-gray-900">
                        {session.game_type.replace('_', ' ').toUpperCase()} Game Exam
                      </h4>
                      {session.status === 'created' && (
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                          Waiting
                        </span>
                      )}
                      {session.status === 'waiting_ready' && (
                        <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold">
                          Starting Soon
                        </span>
                      )}
                      {session.status === 'in_progress' && (
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold animate-pulse">
                          In Progress
                        </span>
                      )}
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-3">
                      Teacher: <span className="font-semibold">{session.teacher_name}</span>
                    </p>

                    {/* My Status */}
                    <div className={`p-3 rounded-lg ${
                      getMyReadyStatus(session) ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'
                    }`}>
                      {getMyReadyStatus(session) ? (
                        <div className="flex items-center gap-2 text-green-700">
                          <CheckCircle className="w-5 h-5" />
                          <span className="font-semibold">You are ready! Waiting for teacher to start...</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-yellow-700">
                          <Clock className="w-5 h-5" />
                          <span className="font-semibold">Click the "I'm Ready" button when you're prepared</span>
                        </div>
                      )}
                    </div>

                    {session.status === 'in_progress' && (
                      <div className="mt-3 bg-green-50 p-3 rounded-lg border border-green-200 animate-pulse">
                        <p className="text-green-800 font-semibold">🎮 Exam started! Numbers are flashing...</p>
                      </div>
                    )}
                  </div>

                  <div className="ml-4">
                    {!getMyReadyStatus(session) && session.status !== 'in_progress' && (
                      <Button
                        onClick={() => handleMarkReady(session.id)}
                        className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-lg"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        I'm Ready
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </Card>
      )}

      {/* Completed Exams with Results */}
      {completedSessions.length > 0 && (
        <Card className="p-6 shadow-xl border-0 bg-gradient-to-br from-emerald-50 via-white to-green-50">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl shadow-lg">
              <Trophy className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                My Exam Results
              </h3>
              <p className="text-sm text-gray-600 mt-1">View your completed exam scores</p>
            </div>
          </div>

          <div className="space-y-4">
            {completedSessions.map(session => (
              <Card key={session.id} className="p-5 border-2 border-emerald-200 bg-white">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-lg font-bold text-gray-900">
                        {session.game_type.replace('_', ' ').toUpperCase()} Game Exam
                      </h4>
                      <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">
                        Completed
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-3">
                      {new Date(session.completed_at).toLocaleString()}
                    </p>

                    {/* My Result */}
                    {myResults[session.id] ? (
                      <div className="bg-gradient-to-r from-emerald-50 to-green-50 p-4 rounded-lg border border-emerald-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-emerald-600 font-semibold mb-1">Your Score</p>
                            <p className="text-3xl font-bold text-emerald-700">
                              {myResults[session.id].score}
                            </p>
                          </div>
                          {myResults[session.id].time_taken && (
                            <div className="text-right">
                              <p className="text-sm text-emerald-600 font-semibold mb-1">Time Taken</p>
                              <p className="text-xl font-bold text-emerald-700">
                                {myResults[session.id].time_taken}s
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <p className="text-gray-600 text-sm">Result not yet available</p>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </Card>
      )}

      {/* No Exams State */}
      {activeSessions.length === 0 && completedSessions.length === 0 && (
        <Card className="p-12 text-center border-0 shadow-xl bg-gradient-to-br from-gray-50 to-white">
          <div className="inline-flex p-4 bg-gray-100 rounded-full mb-4">
            <Trophy className="w-12 h-12 text-gray-400" />
          </div>
          <p className="text-gray-600 text-lg font-medium">No exam sessions yet</p>
          <p className="text-gray-500 text-sm mt-1">
            Your teacher will create exam sessions that will appear here
          </p>
        </Card>
      )}
    </div>
  );
};

export default StudentLiveExam;
